import { Button } from '@/components/ui/Button';
import { Calendar } from '@/components/ui/Calendar';
import { Popover, PopoverContent } from '@/components/ui/Popover';
import { Separator } from '@/components/ui/Separator';
import { cn } from '@/lib/utils';
import {
  browserTimeZoneKey,
  dateTimeInputFormat,
  defaultTimezoneKey,
  Time,
  TimezoneSelectionOption,
  UTC_ZONE,
} from '@/models/time.models';
import { browserTimeZone, getTimezoneOffset } from '@/utils/time';
import { PopoverAnchor } from '@radix-ui/react-popover';
import { CalendarClock } from 'lucide-react';
import { DateTime } from 'luxon';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Input } from '../Input';
import { TimeInput } from './TimeInput';
import { TimezoneSelection } from './TimezoneSelection';

type DateTimePickerProps = {
  defaultDate?: DateTime;
  defaultTimezone?: string;
  className?: string;
  placeholder?: string;
  enableTimePicker?: boolean;
  extraTimezone?: string;
  timezoneSelectionEnabled?: boolean;
  disabled?: boolean;
  error?: string;
  required?: boolean;
  onChange?: (dateTime?: DateTime) => void;
  onTimezoneChange?: (timezone: string) => void;
  hasError?: (error?: boolean) => void;
};

export function DateTimePicker({
  defaultDate,
  extraTimezone,
  className,
  placeholder = dateTimeInputFormat,
  disabled = false,
  error,
  required = false,
  timezoneSelectionEnabled,
  onChange,
  hasError,
}: DateTimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dateTimeString, setDateTimeString] = useState<string>('');
  // selectedMonth is used to control the calendar month, set the view of the current month when selectedCalendarDate changes.
  const [selectedMonth, setSelectedMonth] = useState<Date>();
  // selectedCalendarDate is the date selected in the calendar.
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date>();

  const [selectedTimezone, setSelectedTimezone] = useState<string | undefined>(
    defaultTimezoneKey,
  );
  // pickedTime from the TimePicker component.
  const [pickedTime, setPickedTime] = useState<Time>();
  // Extra timezone option for the TimezoneSelection component, if provided. We use this to show e.g device's timezone.
  const [extraTimezoneOption, setExtraTimezoneOption] =
    useState<TimezoneSelectionOption>();

  const getSelectedTimezone = useCallback(
    (timezone?: string): string | undefined => {
      if (timezoneSelectionEnabled) {
        if (timezone === defaultTimezoneKey) {
          return extraTimezone;
        } else if (timezone === browserTimeZoneKey) {
          return browserTimeZone;
        } else {
          return timezone;
        }
      } else {
        return browserTimeZone;
      }
    },
    [extraTimezone, timezoneSelectionEnabled],
  );

  const formatDateTimeForInput = useCallback(
    (dateTime: DateTime, timezone?: string): string => {
      const resolvedZone = getSelectedTimezone(timezone);
      const displayDateTime = resolvedZone
        ? dateTime.setZone(resolvedZone)
        : dateTime;
      return displayDateTime.toFormat(dateTimeInputFormat);
    },
    [getSelectedTimezone],
  );

  useEffect(() => {
    if (extraTimezone) {
      setExtraTimezoneOption({
        label: `Default (${extraTimezone})`,
        key: defaultTimezoneKey,
        timezone: extraTimezone,
        offset: getTimezoneOffset(extraTimezone),
      });
    }
  }, [extraTimezone]);

  useEffect(() => {
    if (isOpen) {
      if (!defaultDate) {
        // When the picker is opened without a default date, we set the current date and time.
        const timezone = getSelectedTimezone(selectedTimezone);
        const dateTimeNow = DateTime.now().setZone(timezone);
        setSelectedMonth(dateTimeNow.toJSDate());
        setSelectedCalendarDate(dateTimeNow.toJSDate());
        setPickedTime({
          hour: dateTimeNow.hour,
          minute: dateTimeNow.minute,
          second: dateTimeNow.second,
        });
      } else {
        // When the picker is opened with a default date, we set the selected month and calendar date to display the selected month.
        setSelectedMonth(selectedCalendarDate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (defaultDate) {
      setDateTimeString(formatDateTimeForInput(defaultDate, selectedTimezone));
    } else {
      setDateTimeString('');
    }
  }, [defaultDate, formatDateTimeForInput, selectedTimezone]);

  useEffect(() => {
    if (defaultDate) {
      setPickedTime({
        hour: defaultDate.hour,
        minute: defaultDate.minute,
        second: defaultDate.second,
      });
      const calendarDate = defaultDate.setZone(UTC_ZONE).toJSDate();
      setSelectedCalendarDate(calendarDate);
      setSelectedMonth(calendarDate);
    } else {
      setSelectedCalendarDate(undefined);
      setSelectedMonth(undefined);
    }
  }, [defaultDate]);

  const handleConfirmClick = () => {
    if (!selectedCalendarDate) {
      if (onChange) {
        onChange(undefined);
      }
      setIsOpen(false);
      return;
    }
    const datetime = DateTime.fromObject(
      {
        year: selectedCalendarDate?.getFullYear(),
        month: selectedCalendarDate?.getMonth() + 1,
        day: selectedCalendarDate?.getDate(),
        hour: pickedTime?.hour,
        minute: pickedTime?.minute,
        second: pickedTime?.second,
      },
      {
        zone: getSelectedTimezone(selectedTimezone),
      },
    );
    if (onChange) {
      onChange(datetime);
    }
    setIsOpen(false);
    if (hasError) {
      hasError(false);
    }
  };

  const handleClearClick = () => {
    setPickedTime(undefined);
    setSelectedCalendarDate(undefined);
    setSelectedTimezone(defaultTimezoneKey);
    if (hasError) {
      hasError();
    }
  };

  const handleNowClick = () => {
    const now = DateTime.now().setZone(getSelectedTimezone(selectedTimezone));
    setSelectedMonth(now.toJSDate());
    setSelectedCalendarDate(now.toJSDate());
    setPickedTime({
      hour: now.hour,
      minute: now.minute,
      second: now.second,
    });
    if (hasError) {
      hasError();
    }
  };
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value) {
      const dateTime = DateTime.fromFormat(value, dateTimeInputFormat, {
        zone: getSelectedTimezone(selectedTimezone),
      });
      if (dateTime.isValid) {
        if (onChange) {
          onChange(dateTime);
        }
        if (hasError) {
          hasError();
        }
      } else {
        if (hasError) {
          hasError(true);
        }
      }
    } else {
      if (onChange) {
        onChange(undefined);
      }
      if (required) {
        if (hasError) {
          hasError(true);
        }
      }
    }
    setDateTimeString(value);
  };

  const handleTimezoneChange = (timezone?: string) => {
    if (selectedCalendarDate) {
      const dateTime = DateTime.fromObject(
        {
          year: selectedCalendarDate?.getFullYear(),
          month: selectedCalendarDate?.getMonth() + 1,
          day: selectedCalendarDate?.getDate(),
          hour: pickedTime?.hour,
          minute: pickedTime?.minute,
          second: pickedTime?.second,
        },
        {
          zone: getSelectedTimezone(selectedTimezone),
        },
      ).setZone(getSelectedTimezone(timezone));
      setSelectedCalendarDate(dateTime?.toJSDate());
      setPickedTime({
        hour: dateTime?.hour,
        minute: dateTime?.minute,
        second: dateTime?.second,
      });
      setDateTimeString(formatDateTimeForInput(dateTime, timezone));
      setSelectedTimezone(timezone);
    } else {
      setSelectedTimezone(timezone);
      if (defaultDate) {
        setDateTimeString(formatDateTimeForInput(defaultDate, timezone));
      }
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverAnchor asChild>
        <div className={cn(`relative w-[225px]`, className)}>
          <Input
            disabled={disabled}
            className={`pr-8 ${error && 'border-red'}`}
            placeholder={placeholder}
            value={dateTimeString}
            onChange={handleInputChange}
          />
          <Button
            disabled={disabled}
            type="button"
            size={'icon'}
            onClick={() => {
              if (disabled) return;
              setIsOpen(true);
            }}
            variant={'link'}
            className="text-muted-foreground hover:text-primary absolute top-0 right-0 hover:cursor-pointer"
          >
            <CalendarClock className="size-5" />
          </Button>
        </div>
      </PopoverAnchor>

      <PopoverContent className="grid w-auto gap-2 p-0" align="start">
        <div className="m-auto mb-2 flex flex-col">
          <Calendar
            month={selectedMonth}
            onMonthChange={setSelectedMonth}
            mode="single"
            endMonth={new Date(2050, 0)}
            selected={selectedCalendarDate}
            onSelect={(defaultDate) => {
              if (defaultDate) {
                setSelectedCalendarDate(defaultDate);
              }
            }}
            timeZone={getSelectedTimezone(selectedTimezone)}
            captionLayout="dropdown"
          />
          <div className="flex justify-center">
            <TimeInput size={8} time={pickedTime} onChange={setPickedTime} />
          </div>
        </div>

        {timezoneSelectionEnabled && (
          <div className="flex gap-2 px-2">
            <div className="w-[250px]">
              <TimezoneSelection
                size="sm"
                allowToggle={false}
                extraTimezone={extraTimezoneOption}
                timezone={selectedTimezone}
                setTimezone={handleTimezoneChange}
              />
            </div>
          </div>
        )}

        <Separator inert orientation="horizontal" />
        <div className="flex justify-between gap-2 p-2">
          <div className="flex gap-2">
            <Button variant={'outline'} size={'sm'} onClick={handleClearClick}>
              Clear
            </Button>
            <Button variant={'outline'} size={'sm'} onClick={handleNowClick}>
              Now
            </Button>
          </div>
          <div>
            <Button
              variant={'outline'}
              size={'sm'}
              onClick={handleConfirmClick}
            >
              Confirm
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
